import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { ColumnDef } from '@tanstack/react-table'
import { PayoutIcon } from '@/shared/components'
import { SortDirection, SortIcon } from '@/entities/sort'
import {
  PaidWithdrawModel,
  WithdrawStatus,
  WithdrawStatusIcons,
  WithdrawStatusTitles,
} from '../model'
import { PayoutButton } from '@/features/withdraws'
import { getShortenId } from '@/shared/utils/getShortenId'
import { Link } from 'react-router-dom'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

export const getColumnsPending = ({
  onAction,
  loadingIds,
  sort,
  toggleSort,
  isAll = false,
}: {
  onAction: (withdrawId: string) => void
  loadingIds: string[]
  sort?: {
    sortCreatedAt?: SortDirection
    sortAmountTo?: SortDirection
    sortAllowedAt?: SortDirection
  }
  toggleSort?: (key: keyof NonNullable<typeof sort>) => void
  isAll?: boolean
}): ColumnDef<PaidWithdrawModel>[] => [
  {
    accessorKey: 'id',
    header: 'ID заявки',
    cell: ({ row }) => {
      const id = row.original.id
      return (
        <CopyBlock text={id} title={id}>
          <Link to={id} className="text-primary">
            {getShortenId(id)}
          </Link>
        </CopyBlock>
      )
    },
  },
  {
    accessorKey: 'payout',
    header: () => {
      return (
        <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortCreatedAt')}>
          <div className="flex justify-between">
            <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortCreatedAt')}>
              <span>Заявка</span>
              {sort && <SortIcon direction={sort.sortCreatedAt} />}
            </div>
            {!isAll && (
              <div
                className="flex items-center gap-1"
                onClick={() => toggleSort?.('sortAllowedAt')}
              >
                <span>Осталось времени</span>
                <span className="shrink-0">
                  {sort && <SortIcon direction={sort.sortAllowedAt} />}
                </span>
              </div>
            )}
          </div>
        </div>
      )
    },
    cell: ({ row }) => {
      if ([WithdrawStatus.WaitingProcessing].includes(row.original.withdrawStatus))
        return (
          <Link to={row.original.id}>
            <ItemLayout
              icon={<PayoutIcon status={WithdrawStatusIcons[row.original.withdrawStatus]} />}
              title={
                <span className="mr-[10px]">
                  {WithdrawStatusTitles[row.original.withdrawStatus]}
                </span>
              }
              subtitle={formatDate(row.original.assignedToTraderAt)}
              timeLeft={row.original.payingAllowedTill}
              statusForTime="pending"
            />
          </Link>
        )
      if (
        [WithdrawStatus.InProgress, WithdrawStatus.PayingRightNow].includes(
          row.original.withdrawStatus
        )
      )
        return (
          <Link to={row.original.id}>
            <ItemLayout
              icon={<PayoutIcon status={WithdrawStatusIcons[row.original.withdrawStatus]} />}
              title={
                <span className="mr-[10px]">
                  {WithdrawStatusTitles[row.original.withdrawStatus]}
                </span>
              }
              subtitle={formatDate(row.original.acceptedByTraderAt)}
              titleClasses="font-medium"
              timeLeft={row.original.payingAllowedTill}
              statusForTime="inProgress"
            />
          </Link>
        )

      return (
        <Link to={row.original.id}>
          <ItemLayout
            icon={<PayoutIcon status={WithdrawStatusIcons[row.original.withdrawStatus]} />}
            title={
              <span className="mr-[10px]">{WithdrawStatusTitles[row.original.withdrawStatus]}</span>
            }
            subtitle={new Date(row.original.createdAt).toLocaleString('ru-RU')}
          />
        </Link>
      )
    },
  },
  {
    accessorKey: 'amount',
    header: () => {
      return (
        <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortAmountTo')}>
          <span>Сумма</span>
          {sort && <SortIcon direction={sort.sortAmountTo} />}
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col">
          {row.original.factAmountTo &&
          row.original.amountTo.toFixed(2) === row.original.factAmountTo.toFixed(2) ? (
            <CopyBlock text={row.original.amountTo?.toFixed(2)}>
              {formatCurrency(row.original.amountTo ?? 0, row.original.currencyTo)}
            </CopyBlock>
          ) : (
            <CopyBlock text={row.original.factAmountTo?.toFixed(2)}>
              {formatCurrency(row.original.factAmountTo ?? 0, row.original.currencyTo)}
            </CopyBlock>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => {
      const id = row.original.id
      const isLoading = loadingIds.includes(id)
      // Показываем кнопку только для заявок со статусом "Ожидают"
      if (row.original.withdrawStatus !== WithdrawStatus.WaitingProcessing) {
        return null
      }
      return (
        <PayoutButton
          status="waitingProcessing"
          isLoading={isLoading}
          onClick={() => onAction(id)}
        />
      )
    },
  },
]
